import React from 'react';
import IndividualQuotationSummary from './IndividualQuotationSummary';
import { IndividualPricingQuote, quoteMatches } from '../../../services/individualPricingQuotations';

interface LoanSummary {
  individualPricingQuote?: IndividualPricingQuote;
  loanAmount?: number; loanTerm?: number; securityType?: string; calculationMode?: string;
  customLoanType?: string; interestRate?: number; adminFee?: number; interestAmount?: number;
  totalRepayment?: number; commencementDate?: string; dueDate?: string;
  numberOfInstallments?: number; paymentDates?: string[]; loanPurpose?: string;
}
const number = (value?: number) => value == null ? 'Not captured' : value.toLocaleString('en-ZA');
const money = (value?: number) => value == null ? 'Not captured'
  : `R ${value.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const label = (value?: string) => value ? value.replace(/_/g, ' ').toLowerCase().replace(/^./, c => c.toUpperCase()) : 'Not captured';
const date = (value?: string) => {
  if (!value) return 'Not captured';
  const parsed = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  return Number.isNaN(parsed.getTime()) ? 'Invalid date' : parsed.toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' });
};

export default function IndividualLoanSummary({ loan = {} }: { loan?: LoanSummary }) {
  const facts = [
    ['Loan type', 'Personal loan'], ['Loan amount', money(loan.loanAmount)],
    ['Loan term', loan.loanTerm == null ? 'Not captured' : `${number(loan.loanTerm)} days`],
    ['Security', label(loan.securityType)], ['Calculation method', label(loan.calculationMode)],
    ['Repayment type', loan.customLoanType === 'INSTALLMENTS' ? 'Monthly instalments' : label(loan.customLoanType)],
    ['Commencement date', date(loan.commencementDate)], ['Final repayment date', date(loan.dueDate)],
    ['Number of instalments', number(loan.numberOfInstallments)],
  ];
  const pricing = [
    ['Monthly interest rate', loan.interestRate == null ? 'Not captured' : `${loan.interestRate.toFixed(2)}%`],
    ['Admin fee', money(loan.adminFee)],
    ['Interest amount', loan.interestAmount == null ? 'Not calculated' : money(loan.interestAmount)],
    ['Total repayment', loan.totalRepayment == null ? 'Not calculated' : money(loan.totalRepayment)],
  ];
  const rows = (items: string[][]) => <dl className="individual-loan-summary-grid">
    {items.map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{value}</dd></div>)}
  </dl>;
  return <section className="card mb-3" aria-label="Individual loan details">
    <div className="card-header bg-light"><h6 className="mb-0">Loan Details</h6></div>
    <div className="card-body">
      {rows(facts)}
      {loan.individualPricingQuote ? <>
        {!quoteMatches(loan.individualPricingQuote, loan) && <p role="alert">Quotation inputs changed. Calculate a new quotation before submitting.</p>}
        <IndividualQuotationSummary quote={loan.individualPricingQuote} />
      </> : <>
      <h6 className="border-top pt-3">Captured pricing</h6>
      <p className="small text-muted">These are the captured application values, not a verified or approved pricing quote.</p>
      {rows(pricing)}</>}
      <div className="border-top pt-3"><h6>Loan purpose</h6><p className="text-break">{loan.loanPurpose || 'Not captured'}</p></div>
      {Array.isArray(loan.paymentDates) && loan.paymentDates.length > 0 ? <>
        <h6>Payment schedule</h6><ol>{loan.paymentDates.map((value, index) => <li key={`${index}-${value}`}>{date(value)}</li>)}</ol>
      </> : null}
    </div>
  </section>;
}
